import {
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Clock,
  Settings,
} from 'lucide-react';
import { ClientSyncData, OverallSyncStatus, Staleness } from '../../types';
import { formatSyncTime, getStalenessColor } from '../../hooks/useClientSyncData';
import { format, parseISO, addHours } from 'date-fns';

interface SyncStatusHeaderProps {
  syncData: ClientSyncData | null;
  syncing: boolean;
  staleness: Staleness;
  onSync: () => void;
  onOpenSettings?: () => void;
}

export default function SyncStatusHeader({
  syncData,
  syncing,
  staleness,
  onSync,
  onOpenSettings
}: SyncStatusHeaderProps) {
  const getStatusIcon = (status: OverallSyncStatus | undefined) => {
    if (syncing) {
      return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
    }

    switch (status) {
      case 'complete':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'partial':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'failed':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'syncing':
        return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const formatAbsoluteTime = (isoDate: string) => {
    try {
      // parseISO handles UTC → local automatically when formatting
      const date = parseISO(isoDate);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch { return isoDate; }
  };

  const getNextSyncTime = () => {
    if (!syncData?.lastFullSyncCompleted) return null;
    try {
      const lastSync = parseISO(syncData.lastFullSyncCompleted);
      // Use client sync interval if enabled, otherwise assume 2-hour forensic scan schedule
      const interval = syncData?.syncSchedule?.enabled
        ? (syncData.syncSchedule.intervalHours || 4)
        : 2;
      return addHours(lastSync, interval);
    } catch { return null; }
  };

  const getStatusText = () => {
    if (syncing) {
      return 'Syncing...';
    }

    if (!syncData?.lastFullSyncCompleted) {
      return 'Never synced';
    }

    const timeAgo = formatSyncTime(syncData.lastFullSyncCompleted);
    const prefix = syncData.syncStatus === 'partial' ? 'Partial sync' :
                   syncData.syncStatus === 'failed' ? 'Sync failed' : 'Last synced';

    return `${prefix}: ${timeAgo}`;
  };

  const getSyncCategoryStatus = () => {
    if (!syncData) return null;

    const categories = [
      { name: 'Users', status: syncData.users.status, count: syncData.users.count },
      { name: 'Groups', status: syncData.groups.status, count: syncData.groups.count },
      { name: 'Mailboxes', status: syncData.sharedMailboxes.status, count: syncData.sharedMailboxes.count },
      { name: 'Security', status: syncData.securityInfo.status, count: syncData.securityInfo.data ? 1 : 0 },
    ];

    return categories;
  };

  const categories = getSyncCategoryStatus();

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-4 mb-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Status */}
        <div className="flex items-center gap-3">
          {getStatusIcon(syncData?.syncStatus)}
          <div>
            <div className={`font-medium ${getStalenessColor(staleness)}`}>
              {getStatusText()}
            </div>
            {syncing && categories && (
              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                {categories.map((cat) => (
                  <span key={cat.name} className="flex items-center gap-1">
                    {cat.status === 'syncing' ? (
                      <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                    ) : cat.status === 'success' ? (
                      <span className="w-2 h-2 bg-green-500 rounded-full" />
                    ) : cat.status === 'failed' ? (
                      <span className="w-2 h-2 bg-red-500 rounded-full" />
                    ) : (
                      <span className="w-2 h-2 bg-gray-300 rounded-full" />
                    )}
                    {cat.name}
                  </span>
                ))}
              </div>
            )}
            {!syncing && syncData?.lastFullSyncCompleted && (
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>
                  <Clock className="h-3 w-3 inline mr-1" />
                  {formatAbsoluteTime(syncData.lastFullSyncCompleted)}
                  {syncData.lastSyncDurationMs ? ` (${Math.round(syncData.lastSyncDurationMs / 1000)}s)` : ''}
                </div>
                {(() => {
                  const nextSync = getNextSyncTime();
                  if (!nextSync) return null;
                  const now = new Date();
                  const isOverdue = nextSync < now;
                  return (
                    <div className={isOverdue ? 'text-amber-500' : ''}>
                      <RefreshCw className="h-3 w-3 inline mr-1" />
                      Next sync: {isOverdue ? 'overdue — ' : ''}{format(nextSync, 'h:mm a')}
                    </div>
                  );
                })()}
                {/* Data completeness indicator */}
                {(() => {
                  const hasUsers = syncData.users?.status === 'success' && syncData.users.count > 0;
                  const hasGroups = syncData.groups?.status === 'success' && syncData.groups.count > 0;
                  const hasMailboxes = syncData.sharedMailboxes?.status === 'success';
                  const hasSecurity = syncData.securityInfo?.status === 'success';
                  const allGood = hasUsers && hasGroups && hasMailboxes && hasSecurity;
                  if (allGood) {
                    return (
                      <div className="text-green-600 dark:text-green-400">
                        <CheckCircle className="h-3 w-3 inline mr-1" />
                        Full sync complete
                      </div>
                    );
                  }
                  const missing: string[] = [];
                  if (!hasUsers) missing.push('Users');
                  if (!hasGroups) missing.push('Groups');
                  if (!hasMailboxes) missing.push('Mailboxes');
                  if (!hasSecurity) missing.push('Security');
                  return (
                    <div className="text-amber-500">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      Missing: {missing.join(', ')} — click Sync Now
                    </div>
                  );
                })()}
              </div>
            )}
            {!syncing && !syncData?.lastFullSyncCompleted && (
              <div className="text-xs text-amber-500">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                Never synced — click Sync Now to collect all data
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {syncData?.syncSchedule.enabled && (
            <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
              Auto-sync: every {syncData.syncSchedule.intervalHours}h
            </span>
          )}
          {onOpenSettings && (
            <button
              onClick={onOpenSettings}
              className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent"
              title="Sync Settings"
            >
              <Settings className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={onSync}
            disabled={syncing}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      </div>

      {/* Category Details (collapsed by default, show on hover or when partial) */}
      {!syncing && categories && syncData?.syncStatus === 'partial' && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="text-sm text-muted-foreground">
            <span className="font-medium">Sync Summary:</span>
            <div className="flex flex-wrap gap-3 mt-1">
              {categories.map((cat) => (
                <span
                  key={cat.name}
                  className={`flex items-center gap-1 ${
                    cat.status === 'success' ? 'text-green-600' :
                    cat.status === 'failed' ? 'text-red-600' : ''
                  }`}
                >
                  {cat.status === 'success' ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : cat.status === 'failed' ? (
                    <AlertTriangle className="h-4 w-4" />
                  ) : null}
                  {cat.name}: {cat.count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
